import { Download, Loader2 } from 'lucide-react';
import type { ProductKey } from '../../lib/app-registry';
import type { RouterOutputs } from '../../lib/trpc';
import { trpc } from '../../lib/trpc';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';

type Invoice = RouterOutputs['billing']['listInvoices'][number];

interface InvoiceHistoryTableProps {
  organizationId: number;
  productKey: ProductKey;
}

const STATUS_VARIANT: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  paid: 'default',
  open: 'secondary',
  uncollectible: 'destructive',
  void: 'outline',
  draft: 'outline',
};

function formatMoney(cents: number, currency: string): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

function formatDate(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/** An org's invoice history for one product's own subscription — never a sibling product's. */
export function InvoiceHistoryTable({ organizationId, productKey }: InvoiceHistoryTableProps) {
  const { data: invoices, isLoading } = trpc.billing.listInvoices.useQuery(
    { organizationId, productKey },
    { enabled: !!organizationId }
  );

  return (
    <Card className="border-border/50 bg-card/50 backdrop-blur-sm">
      <CardHeader>
        <CardTitle>Orders and invoices</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-10 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Description</TableHead>
                <TableHead>Created on</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Total payable</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!invoices || invoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-10 text-center text-muted-foreground">
                    No results found
                  </TableCell>
                </TableRow>
              ) : (
                invoices.map((invoice: Invoice) => (
                  <TableRow key={invoice.id}>
                    <TableCell>{invoice.number ?? invoice.id}</TableCell>
                    <TableCell>{formatDate(invoice.created)}</TableCell>
                    <TableCell>
                      <Badge variant={STATUS_VARIANT[invoice.status ?? ''] ?? 'outline'}>
                        {invoice.status ?? 'unknown'}
                      </Badge>
                    </TableCell>
                    <TableCell>{formatMoney(invoice.totalCents, invoice.currency)}</TableCell>
                    <TableCell>
                      {invoice.hostedInvoiceUrl && (
                        <Button variant="ghost" size="sm" asChild>
                          <a href={invoice.hostedInvoiceUrl} target="_blank" rel="noreferrer">
                            <Download className="mr-1.5 h-3.5 w-3.5" />
                            View
                          </a>
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
